import type { ServicePartnerPageDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { EntityCard } from "@/components/EntityCard/EntityCard";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { useShareEntityMenuItems } from "@/components/ShareButton/helpers";
import { ShareToChatModal } from "@/components/ShareToChatModal/ShareToChatModal";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { Badge } from "@/design-system/Badge";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface ServiceCardProps {
  service: ServicePartnerPageDto;
  "data-testid"?: string;
  onEdit: () => void;
  onDelete: () => void;
}

export function ServiceCard({
  service,
  "data-testid": dataTestId,
  onEdit,
  onDelete,
}: ServiceCardProps): React.ReactNode {
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);

  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const shareEntityMenuItems = useShareEntityMenuItems({
    deepLinkPayload: {
      entityType: "service",
      entityId: service.id,
    },
    callbacks: {
      shareToCommunityFeed: shareToCommunityFeedModalHandler.setTrue,
      shareToChat: shareToChatModalHandler.setTrue,
    },
  });

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      type: "button",
      isHidden: !service.canEdit,
      callbackFn: onEdit,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      type: "button",
      isHidden: !service.canDelete,
      callbackFn: onDelete,
    },
    ...shareEntityMenuItems,
  ];

  const bannerImg = service.headerImage || sessionUser.project.backgroundImage;

  return (
    <>
      <EntityCard
        data-testid={dataTestId}
        href={routes.servicePartners.details({ slug, id: service.id })}
        title={service.title}
        subtitle={service.subtitle}
        bannerImg={bannerImg}
        metadata={!isDefined(service.viewCount) ? undefined : t("common.label.view", { count: service.viewCount })}
        menuActions={menuActions}
        renderBadges={() => (
          <>
            {service.connection && (
              <Badge key="connection-name" icon={<MarkerPin01Icon />}>
                {service.connection.name}
              </Badge>
            )}
            {service.hasAudience && sessionUser.isAdmin && <Badge icon={<UsersCheckIcon />} />}
          </>
        )}
      />
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityId={service.id}
        entityType="service"
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityId={service.id}
        entityType="service"
      />
    </>
  );
}
